import { useEffect, useState } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { showErrorToast, showSuccessToast } from '@/lib/toast'
import { ApiError } from '@/lib/api/client'
import {
  getImportCategoryMappings,
  retargetImportCategoryMapping,
  type CategorySettingDto,
  type ImportCategoryMappingDto,
} from '@/lib/api/v2/settings'

interface WinnerGroupMappingsProps {
  categories: CategorySettingDto[]
}

/**
 * SOLY-IMPORT-003 S08 — the answers the Winner import remembered: « group 20 → Électro ». Changing
 * one applies to the next imports; quotes already imported keep their sections. Hidden until an
 * import has made one.
 */
export function WinnerGroupMappings({ categories }: WinnerGroupMappingsProps) {
  const [mappings, setMappings] = useState<ImportCategoryMappingDto[]>([])
  const [savingId, setSavingId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getImportCategoryMappings()
      .then((rows) => {
        if (!cancelled) setMappings(rows)
      })
      .catch(() => {
        if (!cancelled) setMappings([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (mappings.length === 0) return null

  const retarget = async (mapping: ImportCategoryMappingDto, categoryId: string) => {
    if (categoryId === mapping.categoryId) return
    setSavingId(mapping.id)
    try {
      const updated = await retargetImportCategoryMapping(mapping.id, categoryId)
      setMappings((prev) => prev.map((m) => (m.id === updated.id ? updated : m)))
      showSuccessToast('Correspondance mise à jour pour les prochains imports.')
    } catch (e) {
      showErrorToast(e instanceof ApiError && e.message ? e.message : "Erreur lors de l'enregistrement.")
    } finally {
      setSavingId(null)
    }
  }

  return (
    <div className="space-y-3 pt-4" data-testid="settings-winner-mappings">
      <div>
        <h3 className="text-sm font-medium">Groupes Winner</h3>
        <p className="text-xs text-muted-foreground">
          La catégorie que l'import Winner donne à chaque groupe. Un changement vaut pour les prochains imports : les devis déjà importés ne bougent pas.
        </p>
      </div>
      <div className="rounded-lg border divide-y">
        {mappings.map((mapping) => {
          const options = categories.filter((c) => !c.isHidden || c.id === mapping.categoryId)
          return (
            <div
              key={mapping.id}
              className="grid gap-2 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_16rem] sm:items-center"
              data-testid={`settings-winner-mapping-row-${mapping.groupNumber}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{mapping.groupName ?? `Groupe ${mapping.groupNumber}`}</div>
                <div className="text-xs text-muted-foreground">Groupe Winner n° {mapping.groupNumber}</div>
              </div>
              <Select value={mapping.categoryId} onValueChange={(value) => retarget(mapping, value)} disabled={savingId === mapping.id}>
                <SelectTrigger
                  aria-label={`Catégorie du groupe ${mapping.groupName ?? mapping.groupNumber}`}
                  data-testid={`settings-winner-mapping-select-${mapping.groupNumber}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.isHidden ? `${c.label} (masquée)` : c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )
        })}
      </div>
    </div>
  )
}
